/* eslint max-statements: ["error", {max: 40}] -- this applies for the entire file */
/* eslint max-lines-per-function: ["error", {max: 240}] -- this applies for the entire file */
/* eslint max-lines: ["error", {max: 301}] -- this applies for the entire file */
/* eslint complexity: ["error", {max: 27}] -- this applies for the entire file */
import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
  TabAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { TabSources } from "@/constants/analytics/Tabs";
import { ModalPath, ScreenPath, TabPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useHelpCenter from "@/hooks/useHelpCenter";
import { useAnalytics } from "@/services/Analytics";
import { useLazyGetClinicalCareTeamQuery } from "@/store/api/clinical";
import {
  useGetFullProfileQuery,
  useGetSafetyPlanExistsQuery,
  useLazyGetSafetyPlanDetailsQuery,
} from "@/store/api/user";
import { useGetUsStatesQuery } from "@/store/api/userEnums";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import CalendarSyncStep from "@/ui/components/onboarding/CalendarSyncStep";
import {
  NotificationSettingsOverviewCard,
  ProfileFooter,
  ProviderCarousel,
  UserOverviewCard,
} from "@/ui/components/profile";
import HelpCenterCard from "@/ui/components/profile/HelpCenterCard";
import { AnalyticsSource } from "@/ui/components/profile/UserOverviewCard";
import {
  FAIconName,
  FloatingButton,
  FullScreenSpinner,
  SectionHeader,
} from "@/ui/core";
import { Box, ScrollView, VStack, WebContentConstraints } from "@/ui/layout";
import { ListDimensions } from "@/ui/layout/list/ListComponents";
import ScreenContainer from "@/ui/layout/ScreenContainer";
import { LayoutToken } from "@/ui/theme/tokens";
import { getCareTeamProps } from "@/utils/profile/careTeam.util";
import { getUserProfileProps } from "@/utils/profile/fullProfile.util";
import {
  getPreferredFirstName,
  getTabSelectorOptions,
} from "@/utils/user.util";

const ComponentName = `${ScreenPath.MainTabs}/${TabPath.Profile}`;

const Screen = () => {
  const { track, trackPageView, trackUserSelectorEvent } = useAnalytics();
  const { isAddToCalendarEnabled } = useFeatureFlags();

  useEffect(() => {
    trackPageView(TabAnalyticsPageView.Profile);
  }, [trackPageView]);
  const appUser = useAppUserSelector();
  const {
    externalId: appUserId,
    isInTreatment: isAppUserInTreatment,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = appUser;
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { presentHelpCenter } = useHelpCenter();

  const userTabs = getTabSelectorOptions({
    appUserId,
    isAppUserPatient,
    patients: proxyPatients,
    shouldIncludeSelf: true,
  });

  const [selectedUserId, setSelectedUserId] = useState<string>(appUserId);
  const {
    data: fullProfileData,
    error: fullProfileError,
    isError: isFullProfileError,
    isLoading: isFullProfileLoading,
  } = useGetFullProfileQuery();

  const safetyPlanUserIds = isAppUserPatient
    ? [appUserId]
    : proxyPatients?.map((p) => p.externalId) || [];
  const { data: safetyPlanExistsData } = useGetSafetyPlanExistsQuery(
    safetyPlanUserIds,
    {
      // When the logout button is pressed, it re-renders the screen,
      // However the isAuthenticated is still true and the Bearer token is expired, so cannot use the isAuthenticated here
      // The safetyPlanUserIds gets set to null immediately after the logout
      // So we need to skip the query if the safetyPlanUserIds is null to avoide 401 error
      // eslint-disable-next-line @typescript-eslint/naming-convention -- skip is a valid option
      skip: !safetyPlanUserIds,
    },
  );
  const [getSafetyPlanDetails] = useLazyGetSafetyPlanDetailsQuery();
  const hasSafetyPlan = safetyPlanExistsData?.[selectedUserId];
  useEffect(() => {
    if (!hasSafetyPlan) return;
    // pre-fetch the safety plan details to have them already cached if the modal is later opened
    void getSafetyPlanDetails(selectedUserId);
  }, [getSafetyPlanDetails, hasSafetyPlan, selectedUserId]);

  // for patient users: show their care team
  // for proxies with 1 patient: show their only patient's care team
  // for proxies with >1 patient:
  //    show nothing for the app user tab,
  //    show the patient's care team for a patient tab
  const isAppUser = selectedUserId === appUserId;
  const { careTeamPatientId, shouldShowProviderCarousel } = getCareTeamProps({
    isAppUser,
    isAppUserInTreatment,
    isAppUserPatient,
    proxyPatients,
    selectedUserId,
  });

  const [fetchClinicalCareTeam, { data }] = useLazyGetClinicalCareTeamQuery();

  useEffect(() => {
    if (!shouldShowProviderCarousel) return;
    void fetchClinicalCareTeam(careTeamPatientId);
  }, [careTeamPatientId, fetchClinicalCareTeam, shouldShowProviderCarousel]);
  const { data: usStates } = useGetUsStatesQuery();

  const providers = data || [];
  const selectedUserProfile = isAppUser
    ? fullProfileData
    : proxyPatients?.find((p) => p.externalId === selectedUserId);

  // if have no data, a spinner is displayed
  // if data is re-fetching, we ignore this and proceed with rendering tab
  // the update will eventually result in a re-render;
  // blocking to ensure data isn't stale is only critical where mutations to data are possible (ie, edit-profile modal)
  if (isFullProfileLoading) {
    return <FullScreenSpinner />;
  }
  // cannot display profile tab if error getting full profile info
  if (isFullProfileError || !isNil(fullProfileError)) {
    throw fullProfileError;
  }
  if (isNil(fullProfileData)) {
    throw new Error(
      "Fetched user's full profile info from API but received empty data.",
    );
  }
  if (isNil(selectedUserProfile)) {
    const appUserError =
      "Fetched app user's full profile info from API but received empty data";
    const selectedUserError = `Selected a patient ID ${selectedUserId} that does not exist in proxyPatients for appUser ${appUserId}`;
    const message = isAppUser ? appUserError : selectedUserError;
    throw new Error(message);
  }

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    trackUserSelectorEvent({
      component: ComponentName,
      selectedUser: userId,
      source: TabSources.Profile,
    });
  };

  const {
    address,
    contactPreferences,
    fullName,
    gender,
    isEmailEnabled,
    isPushEnabled,
    isSmsEnabled,
    languages,
    phoneNumber,
    pronouns,
    userType,
  } = getUserProfileProps({ profile: selectedUserProfile, usStates });

  const handleSafetyPlanPress = async () => {
    if (isNil(selectedUserProfile)) {
      return;
    }

    track(ProfileAnalyticsEvent.SafetyPlanPressed, {
      [AnalyticsFieldName.Component]: ComponentName,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: TabSources.Profile,
    });

    const firstName = getPreferredFirstName({
      chosenName: selectedUserProfile.chosenName,
      firstName: selectedUserProfile.firstName,
    });

    router.navigate({
      params: {
        firstName,
        selectedUserId,
      },
      pathname: ModalPath.SafetyPlanDetails,
    });
  };

  const handleHelpCenterPress = () => {
    void presentHelpCenter({ intercomPresenterSource: TabSources.Profile });
  };

  return (
    <ScreenContainer>
      <Box style={WebContentConstraints}>
        <SectionHeader
          appUserId={appUserId}
          isAppUserSupport={isAppUserSupport}
          onUserChange={handleUserChange}
          testID={ProfileTestID.Primary}
          title="Profile"
          userTabs={userTabs}
        />
      </Box>
      <ScrollView
        // on web, maxWidth+center content; ScrollView stays full-width so scroll works viewport-wide.
        contentContainerStyle={{
          ...WebContentConstraints,
          paddingBottom: hasSafetyPlan
            ? ListDimensions.FABBottomOffset
            : LayoutToken.ContentPaddingHorizontal,
        }}
        paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
      >
        <VStack gap={ListDimensions.SafeVerticalPadding} paddingTop={24}>
          <UserOverviewCard
            address={address}
            analyticsSource={AnalyticsSource.ProfileTab}
            contactPreferences={contactPreferences}
            fullName={fullName}
            gender={gender}
            languages={languages}
            phoneNumber={phoneNumber}
            pronouns={pronouns}
            selectedUserId={selectedUserId}
            userType={userType}
          />
          {isAppUser && (
            <NotificationSettingsOverviewCard
              isEmailEnabled={isEmailEnabled}
              isPushEnabled={isPushEnabled}
              isSmsEnabled={isSmsEnabled}
            />
          )}
          {isAppUser && isAddToCalendarEnabled && (
            <CalendarSyncStep
              testID={ProfileTestID.CalendarPreferencesSection}
              title="Connected calendars"
            />
          )}
          {shouldShowProviderCarousel && (
            <ProviderCarousel providers={providers} />
          )}
          <HelpCenterCard onPress={handleHelpCenterPress} />
          <ProfileFooter selectedUserId={selectedUserId} />
        </VStack>
      </ScrollView>
      {hasSafetyPlan && (
        <FloatingButton
          leftIcon={FAIconName.Shield}
          onPress={handleSafetyPlanPress}
          testID={ProfileTestID.SafetyPlan}
          title="Safety Plan"
        />
      )}
    </ScreenContainer>
  );
};

export default Screen;
